import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ChevronLeft,
  User,
  Mail,
  MapPin,
  Link as LinkIcon,
  Save,
  Crown,
  LogOut,
  Camera,
  CreditCard,
  Calendar,
  Receipt,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  ExternalLink,
  CheckCircle,
  Bell,
  BellOff,
  Zap,
  Send,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { isNativePlatform } from '../lib/platform';
import AppleIAPButton from '../components/AppleIAPButton';

const ProfilePage = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [subscriptionHistory, setSubscriptionHistory] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({
    email_alerts_enabled: true,
    alert_frequency: 'immediate',
    min_match_score: 50
  });
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [profile, setProfile] = useState({
    name: user?.name || '',
    bio: user?.bio || '',
    location: user?.location || '',
    linkedin: user?.linkedin || '',
    website: user?.website || ''
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchSubscriptionHistory();
    if (user?.subscription_tier === 'club' || user?.subscription_tier === 'expert' || user?.subscription_tier === 'founder') {
      fetchNotificationPrefs();
    }
  }, [user?.subscription_tier]);

  const fetchNotificationPrefs = async () => {
    try {
      const response = await axios.get(`${API}/notifications/preferences`, { withCredentials: true });
      setNotificationPrefs(response.data);
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
    }
  };

  const fetchSubscriptionHistory = async () => {
    try {
      const response = await axios.get(`${API}/payments/subscription/history`, { withCredentials: true });
      setSubscriptionHistory(response.data);
    } catch (error) {
      console.error('Error fetching subscription history:', error);
      // Set empty history to stop loading spinner
      setSubscriptionHistory({ current_tier: user?.subscription_tier || 'free', invoices: [] });
    }
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await axios.put(`${API}/auth/profile`, profile, { withCredentials: true });
      updateUser({ ...user, ...response.data });
      toast.success('Profil mis à jour !');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarClick = () => {
    try {
      fileInputRef.current?.click();
    } catch (error) {
      toast.error('Impossible d\'ouvrir le sélecteur de fichiers');
    }
  };

  const handleAvatarChange = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        toast.error('Veuillez sélectionner une image');
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        toast.error('L\'image ne doit pas dépasser 2MB');
        return;
      }

      setAvatarLoading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/auth/upload-avatar`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateUser({ ...user, picture: response.data.picture });
      toast.success('Photo de profil mise à jour !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setAvatarLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, { withCredentials: true });
      
      toast.success('Mot de passe modifié avec succès !');
      setShowPasswordForm(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error('Veuillez entrer votre mot de passe');
      return;
    }
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/auth/account`, {
        data: { password: deletePassword },
        withCredentials: true
      });
      toast.success('Compte supprimé avec succès');
      await logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleNotificationSave = async () => {
    setNotificationLoading(true);
    try {
      await axios.put(`${API}/notifications/preferences`, notificationPrefs, { withCredentials: true });
      toast.success('Préférences de notification mises à jour !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setTestEmailLoading(true);
    try {
      await axios.post(`${API}/notifications/test`, {}, { withCredentials: true });
      toast.success('Email de test envoyé ! Vérifiez votre boîte de réception.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setTestEmailLoading(false);
    }
  };

  const tierLabels = {
    free: { label: 'Gratuit', color: 'bg-slate-100 text-slate-600', price: '0€' },
    pro: { label: 'Pro', color: 'bg-[#0D47A1] text-white', price: '' },
    expert: { label: '10M Investor', color: 'bg-[#00B050] text-white', price: '' }
  };

  const currentTier = tierLabels[user?.subscription_tier] || tierLabels.free;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-[#0D47A1] hover:bg-[#0D47A1]/5">
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Dashboard
                </Button>
              </Link>
              <Link to="/dashboard" className="hidden md:flex">
                <img src="/logo-horizontal.png" alt="10 Millions Investor" className="h-16 sm:h-20 w-auto" data-testid="header-logo" />
              </Link>
              <div className="hidden md:flex items-center gap-2 ml-4 pl-4 border-l border-slate-200">
                <User className="h-5 w-5 text-slate-600" />
                <h1 className="font-semibold text-slate-900">Mon Profil</h1>
              </div>
            </div>
            <Button variant="ghost" onClick={handleLogout} data-testid="profile-logout-btn" className="text-slate-600 hover:text-red-600">
              <LogOut className="h-4 w-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Card with Avatar */}
        <Card className="border-slate-200 mb-6">
          <CardContent className="p-6">
            <div className="flex items-center space-x-6">
              {/* Avatar */}
              <div className="relative">
                <div 
                  className="w-24 h-24 rounded-full overflow-hidden bg-[#0D47A1] flex items-center justify-center cursor-pointer group"
                  onClick={handleAvatarClick}
                >
                  {avatarLoading ? (
                    <Loader2 className="h-8 w-8 text-white animate-spin" />
                  ) : user?.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-3xl font-bold">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-full">
                    <Camera className="h-6 w-6 text-white" />
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                  data-testid="avatar-upload-input"
                />
              </div>

              <div className="flex-1">
                <h2 className="text-xl font-bold text-slate-900">{user?.name}</h2>
                <p className="text-slate-500">{user?.email}</p>
                <div className="mt-2 flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${currentTier.color}`}>
                    {currentTier.label}
                  </span>
                  {!isNativePlatform() && (
                    <span className="text-sm text-slate-500">{currentTier.price}</span>
                  )}
                  {user?.subscription_tier === 'free' && !isNativePlatform() && (
                    <Link to="/pricing">
                      <Button variant="outline" size="sm">
                        <Crown className="h-3 w-3 mr-1" />
                        Passer à Pro
                      </Button>
                    </Link>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Cliquez sur la photo pour la modifier
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit Profile */}
        <Card className="border-slate-200 mb-6">
          <CardHeader>
            <CardTitle>Modifier mon profil</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Nom complet</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={profile.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="pl-10"
                  data-testid="profile-name-input"
                />
              </div>
            </div>

            <div>
              <Label>Bio</Label>
              <Textarea
                placeholder="Parlez-nous de vous et de vos projets d'investissement..."
                rows={3}
                value={profile.bio}
                onChange={(e) => handleChange('bio', e.target.value)}
                data-testid="profile-bio-input"
              />
            </div>

            <div>
              <Label>Localisation</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Paris, France"
                  value={profile.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  className="pl-10"
                  data-testid="profile-location-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>LinkedIn</Label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="https://linkedin.com/in/..."
                    value={profile.linkedin}
                    onChange={(e) => handleChange('linkedin', e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label>Site web</Label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="https://..."
                    value={profile.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            <Button
              onClick={handleSave}
              disabled={loading}
              className="w-full bg-[#0D47A1] hover:bg-[#0A367A]"
              data-testid="save-profile-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </Button>
          </CardContent>
        </Card>

        {/* Password Change */}
        <Card className="border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Lock className="h-5 w-5 mr-2" />
              Sécurité
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!showPasswordForm ? (
              <Button 
                variant="outline" 
                onClick={() => setShowPasswordForm(true)}
                data-testid="change-password-btn"
              >
                Changer le mot de passe
              </Button>
            ) : (
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <Label>Mot de passe actuel</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="pl-10 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                    >
                      {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>Nouveau mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="pl-10 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>Confirmer le nouveau mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    className="bg-[#00B050] hover:bg-[#00853C]"
                    disabled={passwordLoading}
                  >
                    {passwordLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Confirmer
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    }}
                  >
                    Annuler
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Notification Preferences (Expert only) */}
        {(user?.subscription_tier === 'club' || user?.subscription_tier === 'expert' || user?.subscription_tier === 'founder') && (
          <Card className="border-slate-200 mb-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Bell className="h-5 w-5 mr-2" />
                Alertes Email - Biens Immobiliers
              </CardTitle>
              <CardDescription>
                Recevez des alertes quand un nouveau bien correspond à vos critères de recherche IA
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  {notificationPrefs.email_alerts_enabled ? (
                    <Bell className="h-5 w-5 text-[#00B050]" />
                  ) : (
                    <BellOff className="h-5 w-5 text-slate-400" />
                  )}
                  <div>
                    <p className="font-medium text-slate-900">Alertes par email</p>
                    <p className="text-sm text-slate-500">
                      {notificationPrefs.email_alerts_enabled ? 'Activées' : 'Désactivées'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setNotificationPrefs(prev => ({ ...prev, email_alerts_enabled: !prev.email_alerts_enabled }))}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    notificationPrefs.email_alerts_enabled ? 'bg-[#00B050]' : 'bg-slate-300'
                  }`}
                  data-testid="notification-toggle"
                >
                  <span 
                    className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      notificationPrefs.email_alerts_enabled ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {notificationPrefs.email_alerts_enabled && (
                <>
                  {/* Frequency */}
                  <div>
                    <Label className="mb-3 block">Fréquence des alertes</Label>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { value: 'immediate', label: 'Immédiat', icon: Zap, desc: 'Dès qu\'un bien correspond' },
                        { value: 'daily', label: 'Quotidien', icon: Calendar, desc: '1 résumé par jour' },
                        { value: 'weekly', label: 'Hebdomadaire', icon: Calendar, desc: '1 résumé par semaine' }
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setNotificationPrefs(prev => ({ ...prev, alert_frequency: opt.value }))}
                          className={`p-4 rounded-lg border-2 text-left transition-all ${
                            notificationPrefs.alert_frequency === opt.value
                              ? 'border-[#0D47A1] bg-[#0D47A1]/5'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid={`frequency-${opt.value}`}
                        >
                          <opt.icon className={`h-5 w-5 mb-2 ${
                            notificationPrefs.alert_frequency === opt.value ? 'text-[#0D47A1]' : 'text-slate-400'
                          }`} />
                          <p className={`font-medium ${
                            notificationPrefs.alert_frequency === opt.value ? 'text-[#0D47A1]' : 'text-slate-700'
                          }`}>{opt.label}</p>
                          <p className="text-xs text-slate-500 mt-1">{opt.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Minimum Score */}
                  <div>
                    <Label className="mb-3 block">Score de correspondance minimum</Label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="range"
                        min="30"
                        max="90"
                        step="10"
                        value={notificationPrefs.min_match_score}
                        onChange={(e) => setNotificationPrefs(prev => ({ ...prev, min_match_score: parseInt(e.target.value) }))}
                        className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#0D47A1]"
                        data-testid="min-score-slider"
                      />
                      <span className="w-16 text-center font-bold text-[#0D47A1] text-lg">
                        {notificationPrefs.min_match_score}%
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 mt-2">
                      Recevez des alertes uniquement pour les biens avec un score ≥ {notificationPrefs.min_match_score}%
                    </p>
                  </div>
                </>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                <Button
                  onClick={handleNotificationSave}
                  disabled={notificationLoading}
                  className="bg-[#0D47A1] hover:bg-[#0A367A]"
                  data-testid="save-notification-btn"
                >
                  {notificationLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Enregistrer les préférences
                </Button>
                <Button
                  onClick={handleTestEmail}
                  disabled={testEmailLoading}
                  variant="outline"
                  data-testid="test-email-btn"
                >
                  {testEmailLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Envoyer un email de test
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Subscription History - different on mobile (Apple IAP) vs web (Square) */}
        {isNativePlatform() ? (
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center">
                <CreditCard className="h-5 w-5 mr-2" />
                Abonnement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-semibold text-slate-900">Plan actuel : {currentTier.label}</p>
                <p className="text-sm text-slate-500 mt-1">Gérez votre abonnement dans les réglages de votre iPhone : Réglages &gt; Apple ID &gt; Abonnements</p>
              </div>
              {user?.subscription_tier === 'free' && (
                <div className="mt-4">
                  <AppleIAPButton onPurchaseSuccess={() => window.location.reload()} />
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CreditCard className="h-5 w-5 mr-2" />
              Historique d'abonnement
            </CardTitle>
          </CardHeader>
          <CardContent>
            {subscriptionHistory ? (
              <div className="space-y-6">
                {/* Current Plan */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-900">Plan actuel : {currentTier.label}</p>
                      {subscriptionHistory.subscription_date && (
                        <p className="text-sm text-slate-500 flex items-center mt-1">
                          <Calendar className="h-4 w-4 mr-1" />
                          Abonné depuis le {subscriptionHistory.subscription_date}
                        </p>
                      )}
                      {subscriptionHistory.current_period_end && (
                        <p className="text-sm text-slate-500">
                          Prochain renouvellement : {subscriptionHistory.current_period_end}
                        </p>
                      )}
                    </div>
                    {!isNativePlatform() && (
                    <Link to="/pricing">
                      <Button variant="outline" size="sm">
                        Gérer
                      </Button>
                    </Link>
                    )}
                  </div>
                </div>

                {/* Payment Method */}
                {subscriptionHistory.payment_method && (
                  <div>
                    <h4 className="font-medium text-slate-700 mb-2 flex items-center">
                      <CreditCard className="h-4 w-4 mr-2" />
                      Moyen de paiement
                    </h4>
                    <div className="p-3 border border-slate-200 rounded-lg flex items-center space-x-3">
                      <div className="w-10 h-6 bg-slate-100 rounded flex items-center justify-center text-xs font-bold">
                        {subscriptionHistory.payment_method.brand}
                      </div>
                      <div>
                        <p className="font-medium">•••• •••• •••• {subscriptionHistory.payment_method.last4}</p>
                        <p className="text-sm text-slate-500">
                          Expire {subscriptionHistory.payment_method.exp_month}/{subscriptionHistory.payment_method.exp_year}
                        </p>
                      </div>
                      <CheckCircle className="h-5 w-5 text-[#00B050] ml-auto" />
                    </div>
                  </div>
                )}

                {/* Invoices */}
                {subscriptionHistory.invoices && subscriptionHistory.invoices.length > 0 && (
                  <div>
                    <h4 className="font-medium text-slate-700 mb-2 flex items-center">
                      <Receipt className="h-4 w-4 mr-2" />
                      Historique des paiements
                    </h4>
                    <div className="space-y-2">
                      {subscriptionHistory.invoices.map((invoice, idx) => (
                        <div 
                          key={idx}
                          className="p-3 border border-slate-200 rounded-lg flex items-center justify-between"
                        >
                          <div>
                            <p className="font-medium">{invoice.amount.toFixed(2)} €</p>
                            <p className="text-sm text-slate-500">{invoice.date}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 text-xs rounded ${
                              invoice.status === 'paid' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {invoice.status === 'paid' ? 'Payé' : invoice.status}
                            </span>
                            {invoice.pdf_url && (
                              <a 
                                href={invoice.pdf_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[#0D47A1] hover:underline flex items-center"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No subscription */}
                {user?.subscription_tier === 'free' && (
                  <div className="text-center py-4">
                    <p className="text-slate-500 mb-4">Vous n'avez pas encore d'abonnement actif</p>
                    <Link to="/pricing">
                      <Button className="bg-[#0D47A1] hover:bg-[#0A367A]">
                        <Crown className="h-4 w-4 mr-2" />
                        Voir les offres
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            )}
          </CardContent>
        </Card>
        )}
        {/* Delete Account */}
        <Card className="border-red-200 mt-6">
          <CardHeader>
            <CardTitle className="flex items-center text-red-600">
              <Trash2 className="h-5 w-5 mr-2" />
              Supprimer mon compte
            </CardTitle>
            <CardDescription>
              Cette action est irréversible. Toutes vos données seront définitivement supprimées.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!showDeleteConfirm ? (
              <Button
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="delete-account-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer mon compte
              </Button>
            ) : (
              <div className="space-y-4 p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-red-800">Attention !</p>
                    <p className="text-sm text-red-700 mt-1">
                      Cette action supprimera définitivement votre compte, vos projets MDB, vos alertes et toutes vos données. Cette action est irréversible.
                    </p>
                  </div>
                </div>
                <div>
                  <Label className="text-red-700">Confirmez avec votre mot de passe</Label>
                  <Input
                    type="password"
                    placeholder="Votre mot de passe"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    className="border-red-300"
                    data-testid="delete-password-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading || !deletePassword}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="confirm-delete-btn"
                  >
                    {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    Supprimer définitivement
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); }}
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ProfilePage;
